/**
 * 住所関連のユーティリティ関数
 *
 * FL-188: 陸送員スケジュール表示項目の拡充
 * 住所から都道府県を抽出する機能を提供
 */

/**
 * 日本の47都道府県の正規表現パターン
 */
const PREFECTURE_REGEX =
  /(北海道|青森県|岩手県|宮城県|秋田県|山形県|福島県|茨城県|栃木県|群馬県|埼玉県|千葉県|東京都|神奈川県|新潟県|富山県|石川県|福井県|山梨県|長野県|岐阜県|静岡県|愛知県|三重県|滋賀県|京都府|大阪府|兵庫県|奈良県|和歌山県|鳥取県|島根県|岡山県|広島県|山口県|徳島県|香川県|愛媛県|高知県|福岡県|佐賀県|長崎県|熊本県|大分県|宮崎県|鹿児島県|沖縄県)/;

/**
 * 政令指定都市から都道府県へのマッピング
 * 住所に都道府県名が含まれていない場合のフォールバック用
 */
const CITY_TO_PREFECTURE_MAP: Record<string, string> = {
  // 北海道
  札幌市: '北海道',
  // 東北
  仙台市: '宮城県',
  // 関東
  さいたま市: '埼玉県',
  千葉市: '千葉県',
  横浜市: '神奈川県',
  川崎市: '神奈川県',
  相模原市: '神奈川県',
  // 中部
  新潟市: '新潟県',
  静岡市: '静岡県',
  浜松市: '静岡県',
  名古屋市: '愛知県',
  // 関西
  京都市: '京都府',
  大阪市: '大阪府',
  堺市: '大阪府',
  神戸市: '兵庫県',
  // 中国
  岡山市: '岡山県',
  広島市: '広島県',
  // 四国
  // なし
  // 九州
  北九州市: '福岡県',
  福岡市: '福岡県',
  熊本市: '熊本県',
};

/**
 * 住所文字列から都道府県を抽出する
 *
 * @param address - 住所文字列（例: "埼玉県春日部市豊町1-1-1"）
 * @returns 都道府県名（例: "埼玉県"）。抽出できない場合は"不明"
 *
 * @example
 * // 都道府県名が含まれている場合
 * extractPrefecture("埼玉県春日部市豊町1-1-1") // => "埼玉県"
 * extractPrefecture("東京都港区赤坂1-1-1") // => "東京都"
 *
 * // 政令指定都市名から推測（フォールバック）
 * extractPrefecture("京都市中京区三条通1-1") // => "京都府"
 * extractPrefecture("横浜市中区本町1-1") // => "神奈川県"
 *
 * // 抽出不可の場合
 * extractPrefecture("無効な住所") // => "不明"
 */
export function extractPrefecture(address: string): string {
  if (!address || typeof address !== 'string') {
    return '不明';
  }

  // 1. 都道府県正規表現でマッチ（優先）
  const prefMatch = address.match(PREFECTURE_REGEX);
  if (prefMatch) {
    return prefMatch[1];
  }

  // 2. 政令指定都市名からの推測（フォールバック）
  for (const [city, pref] of Object.entries(CITY_TO_PREFECTURE_MAP)) {
    if (address.includes(city)) {
      return pref;
    }
  }

  return '不明';
}

/**
 * 住所文字列に都道府県が含まれているか確認する
 *
 * @param address - 住所文字列
 * @returns 都道府県が含まれている場合true
 *
 * @example
 * hasPrefecture("埼玉県春日部市") // => true
 * hasPrefecture("春日部市豊町") // => false
 */
export function hasPrefecture(address: string): boolean {
  if (!address || typeof address !== 'string') {
    return false;
  }

  return PREFECTURE_REGEX.test(address);
}
